"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import Link from 'next/link'
//import { usePathname } from 'next/navigation'
//import dynamic from 'next/dynamic'
import clsx from 'clsx'
import { motion, MotionConfig/*, useReducedMotion */} from 'framer-motion'

import { Button } from '@/components/common/Button'
import { Container } from '@/components/common/Container'
import { Footer } from '@/components/Footer'
import { GridPattern } from '@/components/GridPattern'
import { Logo, Logomark } from '@/components/Logo'
import { Offices } from '@/components/Offices'
import ItalianFlag from '@/images/country/italy-flag.svg'
import UsaFlag from '@/images/country/usa-flag.svg'


import { useLanguage } from "@/context/LanguageContext";
import Image from 'next/image'

const texts = {
  en: {
    title: 'Let’s work together',
    contactUs: 'Contact me',
    text: 'We can’t wait to hear from you.',
    emailUs: 'Email us',
    enterpriseDetails: 'Enterprise details',
    workInquiries: 'Work inquiries',
    name: 'Name',
    company: 'Company',
    phone: 'Phone',
    message: 'Message',
    budget: 'Budget',
    moreThan: 'More than',
    currency: '$',
  },
  it: {
    title: 'Lavoriamo insieme',
    contactUs: 'Contattami',
    text: 'Non vediamo l\'ora di sentirti.',
    emailUs: 'Scrivici',
    enterpriseDetails: 'Dettagli azienda',
    workInquiries: 'Richieste di lavoro',
    name: 'Nome',
    company: 'Azienda',
    phone: 'Telefono',
    message: 'Messaggio',
    budget: 'Budget',
    moreThan: 'Più di',
    currency: '€',
  },
};

const RootLayoutContext = createContext<{
  logoHovered: boolean
  setLogoHovered: React.Dispatch<React.SetStateAction<boolean>>
} | null>(null)

function XIcon(props: React.ComponentPropsWithoutRef<'svg'>) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path d="m5.636 4.223 14.142 14.142-1.414 1.414L4.222 5.637z" />
      <path d="M4.222 18.363 18.364 4.22l1.414 1.414L5.636 19.777z" />
    </svg>
  )
}

function MenuIcon(props: React.ComponentPropsWithoutRef<'svg'>) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path d="M2 6h20v2H2zM2 16h20v2H2z" />
    </svg>
  )
}

function Header({
  panelId,
  icon: Icon,
  expanded,
  onToggle,
  toggleRef,
  invert = false,
}: {
  panelId: string
  icon: React.ComponentType<{ className?: string }>
  expanded: boolean
  onToggle: () => void
  toggleRef: React.RefObject<HTMLButtonElement | null>
  invert?: boolean
}) {
  const { language, setLanguage } = useLanguage();
  let { logoHovered, setLogoHovered } = useContext(RootLayoutContext)!

  return (
    <Container>
      <div className="flex items-center justify-between">
        <Link
          href="/"
          aria-label="Home"
          onMouseEnter={() => setLogoHovered(true)}
          onMouseLeave={() => setLogoHovered(false)}
        >
          <Logomark
            className="h-8 sm:hidden"
            invert={invert}
            filled={logoHovered}
          />
          <Logo
            className="hidden h-8 sm:block"
            invert={invert}
            filled={logoHovered}
          />
        </Link>
        <div className="flex items-center gap-x-8">
          {(language === 'en' ? (
              <>
                <Button href="/contact" invert={invert}>
                  <span>
                    {texts[language as keyof typeof texts]?.contactUs ||
                    texts['en'].contactUs}
                  </span>
                </Button>
                <Button invert={invert} onClick={() => setLanguage('it')}>
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    IT
                    <Image
                      src={ItalianFlag}
                      alt="Italiano"
                      width={21}
                      height={14}
                      loading="eager"
                      decoding="async"
                      style={{ display: 'block' }}
                    />
                  </span>
                </Button>
              </>
            ) : language === 'it' ? (
              <>
                <Button href="/contact" invert={invert}>
                  <span>
                    {texts[language as keyof typeof texts]?.contactUs ||
                    texts['en'].contactUs}
                  </span>
                </Button>
                <Button invert={invert} onClick={() => setLanguage('en')}>
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    EN
                    <Image
                      src={UsaFlag}
                      alt="English"
                      width={21}
                      height={14}
                      loading="eager"
                      decoding="async"
                      style={{ display: 'block' }}
                    />
                  </span>
                </Button>
              </>
            ) : null)}

          <button
            ref={toggleRef}
            type="button"
            onClick={onToggle}
            aria-expanded={expanded ? 'true' : 'false'}
            aria-controls={panelId}
            className={clsx(
              'group -m-2.5 rounded-full p-2.5 transition',
              invert ? 'hover:bg-white/10' : 'hover:bg-neutral-950/10',
            )}
            aria-label="Toggle navigation"
          >
            <Icon
              className={clsx(
                'h-6 w-6',
                invert
                  ? 'fill-white group-hover:fill-neutral-200'
                  : 'fill-neutral-950 group-hover:fill-neutral-700',
              )}
            />
          </button>
        </div>
      </div>
    </Container>
  )
}

function RootLayoutInner({ children }: { children: React.ReactNode }) {
  let panelId = useId()
  let [expanded, setExpanded] = useState(false)
  let openRef = useRef<React.ElementRef<'button'>>(null)
  let closeRef = useRef<React.ElementRef<'button'>>(null)
  let navRef = useRef<React.ElementRef<'div'>>(null)
  //let shouldReduceMotion = useReducedMotion()
  let [shouldReduceMotion, setShouldReduceMotion] = useState(false)

  useEffect(() => {
    setShouldReduceMotion(
        window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false
    )
  }, [])

  const { language } = useLanguage();

  useEffect(() => {
    function onClick(event: MouseEvent) {
      if (
        event.target instanceof HTMLElement &&
        event.target.closest('a')?.href === window.location.href
      ) {
        setExpanded(false)
      }
    }

    window.addEventListener('click', onClick)

    return () => {
      window.removeEventListener('click', onClick)
    }
  }, [])

  return (
    <MotionConfig transition={shouldReduceMotion ? { duration: 0 } : undefined}>
      <header>
        <div
          className="absolute top-2 right-0 left-0 z-40 pt-14"
          aria-hidden={expanded ? 'true' : undefined}
        >
          <Header
            panelId={panelId}
            icon={MenuIcon}
            toggleRef={openRef}
            expanded={expanded}
            onToggle={() => {
              setExpanded((expanded) => !expanded)
              window.setTimeout(() =>
                closeRef.current?.focus({ preventScroll: true }),
              )
            }}
          />
        </div>

        <motion.div
          layout
          id={panelId}
          style={{ height: expanded ? 'auto' : '0.5rem' }}
          className="relative z-50 overflow-hidden bg-neutral-950 pt-2"
          aria-hidden={expanded ? undefined : 'true'}
        >
          <motion.div layout className="bg-neutral-800">
            <div ref={navRef} className="bg-neutral-950 pt-14 pb-16">
              <Header
                invert
                panelId={panelId}
                icon={XIcon}
                toggleRef={closeRef}
                expanded={expanded}
                onToggle={() => {
                  setExpanded((expanded) => !expanded)
                  window.setTimeout(() =>
                    openRef.current?.focus({ preventScroll: true }),
                  )
                }}
              />
            </div>
            <div className="relative bg-neutral-950 before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-neutral-800">
              <Container>
                <div className="grid grid-cols-1 gap-y-10 pt-10 pb-16 sm:grid-cols-2 sm:pt-16">
                  <div>
                    <h2 className="font-display text-base font-semibold text-white">
                      {texts[language as keyof typeof texts].enterpriseDetails || texts['en'].enterpriseDetails}
                    </h2>
                    <Offices
                      invert
                      className="mt-6 grid grid-cols-1 gap-8 sm:grid-cols-2"
                    />
                  </div>
                </div>
              </Container>
            </div>
          </motion.div>
        </motion.div>
      </header>

      <motion.div
        layout
        style={{ borderTopLeftRadius: 40, borderTopRightRadius: 40 }}
        className="relative flex flex-auto overflow-hidden bg-white pt-14"
      >
        <motion.div
          layout
          className="relative isolate flex w-full flex-col pt-9"
        >
          <GridPattern
            className="absolute inset-x-0 -top-14 -z-10 h-[1000px] w-full fill-neutral-50 stroke-neutral-950/5 [mask-image:linear-gradient(to_bottom_left,white_40%,transparent_50%)]"
            yOffset={-96}
            interactive
          />

          <main className="w-full flex-auto">{children}</main>

          <Footer />
        </motion.div>
      </motion.div>
    </MotionConfig>
  )
}

export function RootLayout({ children }: { children: React.ReactNode }) {
  let [logoHovered, setLogoHovered] = useState(false)

  return (
    //<LanguageProvider>
      <RootLayoutContext.Provider value={{ logoHovered, setLogoHovered }}>
        <RootLayoutInner>
          {children}
        </RootLayoutInner>
      </RootLayoutContext.Provider>
    //</LanguageProvider>
  )
}
